import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const loadingMessages = [
  "Searching colorful fairy tales...",
  "Looking for the perfect story...",
  "Finding the perfect unicorn...",
  "Turning the page...",
  "Whispering to the Wise Old Bear...",
  "Checking the Story Index...",
  "Finding the Hidden Chapter...",
  "Dusting off a Classic Tale...",
  "Warming up the narrator's voice...",
  "Tuning in the Magical Radio...",
  "Adjusting the Soundwaves...",
  "Shhh... the story is beginning!",
  "Fluffing the cozy chair for listening...",
  "Riding a dragon to the next page...",
  "Brewing a Story Potion...",
  "Counting stars for the night-time tale...",
  "Getting the magic just right...",
  "Almost ready for adventure!"
];

interface LocationState {
  prefilledCategory?: string;
  prefilledSubOption?: string;
  prefilledDetails?: string;
  includeChild?: boolean;
  childName?: string | null;
}

const CreateStory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [waitingForAudio, setWaitingForAudio] = useState(false);
  const [storySlug, setStorySlug] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string>('free');
  const [whimsicalMessage, setWhimsicalMessage] = useState(() => 
    loadingMessages[Math.floor(Math.random() * loadingMessages.length)]
  );

  // Rotate whimsical messages every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setWhimsicalMessage(prev => {
        let newMessage;
        do {
          newMessage = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
        } while (newMessage === prev);
        return newMessage;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Fetch user's plan to enforce includeChild restriction for Basic users
  useEffect(() => {
    const fetchPlan = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: planData } = await supabase.rpc('get_effective_plan', { user_uuid: user.id });
        if (planData && planData.length > 0) {
          setCurrentPlan(planData[0].plan || 'free');
        }
      }
    };
    fetchPlan();
  }, []);

  const state = location.state as LocationState;
  const category = state?.prefilledCategory;
  const subOption = state?.prefilledSubOption;
  const customDetailsFromState = state?.prefilledDetails || "";
  const includeChildFromState = state?.includeChild || false;
  const childName = state?.childName;
  
  // Override customDetails and includeChild for Basic plan users (server-side safety check)
  const customDetails = currentPlan !== 'basic' ? customDetailsFromState : "";
  const includeChild = currentPlan !== 'basic' && includeChildFromState;

  useEffect(() => {
    // If no data from Home page, redirect back
    if (!category || !subOption) {
      navigate("/");
    }
  }, [category, subOption, navigate]);

  const handleGenerateStory = async () => {
    setLoading(true);
    setProgressMessage("Generating story text...");

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      // Build the character name
      const characterName = includeChild && childName ? childName : "the main character";

      // Build qualities array with topic (category) first, then subtopic, then custom details if provided
      const qualities = customDetails 
        ? [subOption, customDetails]
        : [subOption];

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-story`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            characterName,
            qualities,
            tempo: "calm",
            stream: true,
            includeChild: includeChild && !!childName,
          }),
        }
      );

      if (!response.ok || !response.body) {
        throw new Error("Failed to start story generation");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let storySlug = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();

          try {
            const parsed = JSON.parse(jsonStr);
            
            if (parsed.progress) {
              setProgressMessage(parsed.progress);
            }
            
            if (parsed.complete && parsed.data) {
              storySlug = parsed.data.storySlug;
            }
            
            if (parsed.error) {
              throw new Error(parsed.error);
            }
          } catch (e) {
            if (e instanceof SyntaxError) {
              textBuffer = line + "\n" + textBuffer;
              break;
            }
            throw e;
          }
        }
      }

      if (!storySlug) {
        throw new Error("No story slug received");
      }

      // Check if first chunk is already ready
      const { data: storyData } = await supabase
        .from('stories')
        .select('audio_chunks')
        .eq('slug', storySlug)
        .single();

      const chunks = storyData?.audio_chunks as Array<{ status: string }> | null;
      if (chunks && chunks[0]?.status === 'ready') {
        // First chunk already ready, navigate immediately
        toast({
          title: "Story Ready!",
          description: "Your story is ready to play",
        });
        navigate(`/story/${storySlug}`, { replace: true });
        return;
      }

      // Wait for first audio chunk
      setProgressMessage("Generating audio...");
      setStorySlug(storySlug);
      setWaitingForAudio(true);
    } catch (error: any) {
      console.error("Error creating story:", error);
      
      // Check if it's a limit reached error
      if (error.message?.includes('limit') || error.message?.includes('LIMIT_REACHED')) {
        // Early-bird users see a toast instead of upgrade dialog
        if (currentPlan === 'early-bird') {
          toast({
            title: "Monthly Limit Reached",
            description: "You've reached your limit of 400 stories this month. Your limit will reset next month.",
            variant: "destructive",
          });
          navigate('/');
          return;
        }
        navigate('/', { state: { showUpgrade: true } });
        return;
      }
      
      toast({
        title: "Error",
        description: "Failed to create story. Please try again.",
        variant: "destructive",
      });
      setLoading(false);
      setProgressMessage("");
    }
  };

  useEffect(() => {
    // Auto-start generation when component mounts
    if (category && subOption) {
      handleGenerateStory();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscribe to realtime updates for first audio chunk
  useEffect(() => {
    if (!storySlug || !waitingForAudio) return;

    const channel = supabase
      .channel(`story-audio-${storySlug}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'stories',
          filter: `slug=eq.${storySlug}`
        },
        (payload) => {
          const newChunks = payload.new.audio_chunks as Array<{ status: string }> | null;
          if (newChunks && newChunks[0]?.status === 'ready') {
            toast({
              title: "Story Ready!",
              description: "Your story is ready to play",
            });
            navigate(`/story/${storySlug}`, { replace: true });
          }
        }
      )
      .subscribe();

    // Fallback timeout after 2 minutes
    const timeout = setTimeout(() => {
      toast({
        title: "Taking longer than expected",
        description: "Navigating to story player...",
      });
      navigate(`/story/${storySlug}`, { replace: true });
    }, 120000);

    return () => {
      supabase.removeChannel(channel);
      clearTimeout(timeout);
    };
  }, [storySlug, waitingForAudio, navigate, toast]);

  if (!category || !subOption) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        <div className="relative w-full max-w-xs mx-auto">
          <div className="absolute inset-0 rounded-2xl bg-primary/30 blur-xl animate-pulse" />
          <video
            src="/loader-bear.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="relative w-full rounded-2xl animate-fade-in"
          />
        </div>
        <p key={whimsicalMessage} className="text-lg text-white/80 mt-6 animate-slide-up-fade">
          {whimsicalMessage}
        </p>
      </div>
    </div>
  );
};

export default CreateStory;
